import type { CustomRouteMap } from '@alltrails/types/CustomRoute';
import type { SegmentPolyline } from '../../types/CustomRoute';
import { decodeSegmentLatLng } from '../legacyGeoJSONConversions';
import { getLineColor } from './colors';

export type NormalizedLine = CustomRouteMap['routes'][number];

// Shared comparator for ordering routes and line segments by sequence_num. getSegmentsForMap zips
// route metadata and elevation data onto these segments by index, so both must sort the same way.
export const bySequenceNum = (a: { sequence_num: number }, b: { sequence_num: number }) => a.sequence_num - b.sequence_num;

// Custom routes return polyline data under `routes`; recordings (presentationType: 'track')
// return it under `tracks` with renamed keys (`lineTimedSegments`/`lineTimedGeoStats`).
// Normalize tracks into the route shape here so downstream code stays route-shaped.
export const getLines = (map: Pick<CustomRouteMap, 'routes' | 'tracks'>): NormalizedLine[] => {
  if (map.routes && map.routes.length > 0) {
    return map.routes;
  }
  if (!map.tracks || map.tracks.length === 0) {
    return [];
  }
  return map.tracks.map(track => ({
    ...track,
    lineSegments: track.lineTimedSegments ?? [],
    lineGeoStats: track.lineTimedGeoStats ?? { distanceTotal: 0, elevationGain: 0 }
  }));
};

/**
 * Lightweight version of getSegmentsForMap that extracts only what's needed to draw a route's
 * polylines: each segment's color and decoded paths. getSegmentsForMap builds on this, layering
 * elevation and metadata on top. This skips decoding the elevation data.
 */
export function getSegmentPolylinesForMap(map: Pick<CustomRouteMap, 'routes' | 'tracks'>): SegmentPolyline[] {
  return [...getLines(map)].sort(bySequenceNum).map(route => {
    const rawColor = route.lineDisplayProperty?.color;
    const color = rawColor ? getLineColor(rawColor) : undefined;
    const paths = [...route.lineSegments]
      .sort(bySequenceNum)
      .map(segment => ({ points: decodeSegmentLatLng(segment), freeDraw: segment.freeDraw ?? undefined }));
    return { color, paths } as SegmentPolyline;
  });
}
